/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComparisonTable } from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/ComparisonTable';
import { SingSaverRewardsSectionId } from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/constants';
import {
  ComparisonTableProduct,
  ComparisonTableProps,
  ComponentComparisonTable,
  OnRewardColumnClickHandler,
  OnRowClickHandler,
} from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/types';
import { getRewardColumnComponentTitle } from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/utils';
import { ApprovalOddsDrawer } from '@hyphengroup/cms-components/components/ApprovalOddsDrawer';
import { useProductBlockAsideModal } from '@hyphengroup/cms-components/components/ProductBlockAsideModal';
import RewardAsideModal from '@hyphengroup/cms-components/components/RewardAsideModal/RewardAsideModal';
import { SlideUpModalHandle } from '@hyphengroup/cms-components/components/SlideUpModal';
import useOpenRewardModal from '@hyphengroup/cms-components/hooks/useOpenRewardModal';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '@hyphengroup/cms-components/utils/analytics/sendAnalytics';
import { filterOffersByAmountAndTenure } from '@hyphengroup/cms-components/utils/helpers/offers';
import { LoanSearchParams } from '@hyphengroup/cms-components/utils/searchParams/loanSearchParams';
import React, { useEffect, useMemo, useState } from 'react';
import { useSafeSearchParams } from '../../../../../hooks/useSafeSearchParams/useSafeSearchParams';
import { useClientContext } from '../../../../modules/context/client';
import { RecalculateProductAttributes } from '../../helpers/calculateComparisonAttribute';
import { getLoanSearchParamsWithDefaultComponentParam } from '../../helpers/loanSearchParams';
import { cmsQueryParamsPrefix } from '../../helpers/types';
import { useLoanSearchParams } from '../../helpers/useLoanSearchParams';
import { pushDataLayer } from '../../pushDataLayer';
import { PageType } from '../../types';
import { PersonalLoanFilterWrapper } from '../PersonalLoanFilter/wrapper';
import { TabsWrapper } from '../TableTabs/wrapper';
import { ApprovalOddsFooter } from './ApprovalOddsFooter';
import {
  useApprovalOdds,
  useApprovalOddsRetrieval,
} from '$/(cms)/_modules/approvalOdds';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { CalculatePersonalLoansRes } from '$/(cms)/_modules/cmsService/pageDataService/types';
import { CmsProduct, SystemAttribute } from '$/(cms)/_modules/cmsService/types';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';

type ComparisonLoanSearchParams = Partial<LoanSearchParams> | undefined;

const setPLAttributeValues = (
  calculatedPersonalLoans: CalculatePersonalLoansRes,
  source: ComparisonTableProduct[],
) => {
  if (!calculatedPersonalLoans.hasError && calculatedPersonalLoans.data) {
    const products = source.map((prod, i) => {
      const newProduct: CmsProduct = { ...prod };
      const resultAttr = calculatedPersonalLoans.data![i];
      newProduct.attributes = RecalculateProductAttributes(
        newProduct.attributes,
        resultAttr,
      );

      return newProduct;
    });

    return {
      calculateError: false,
      products,
    };
  }

  return {
    calculateError: true,
    products: source,
  };
};

export function ComparisonTableWrapper(
  props: Omit<
    ComparisonTableProps,
    | 'activeAttribute'
    | 'onRewardColumnClick'
    | 'onRowClick'
    | 'isRowSelected'
    | 'pushDataLayer'
  > & {
    pageType: PageType;
    tabs: string[] | undefined;
  },
) {
  const { products: preProducts, loanSearchParams, ...rest } = props;
  const { pageData, isPreview, publicCmsConfig } = usePageDataContext();
  const oddsDrawerRef = React.useRef<SlideUpModalHandle>(null);
  const { brand, langPrefix } = useClientContext();

  const {
    tabs,
    tableConfig: {
      min_loan_amount,
      max_loan_amount,
      min_loan_tenure,
      max_loan_tenure,
    },
    vertical,
    slug,
    blockIndex,
  } = rest;

  let loanSearchParamsToWork: ComparisonLoanSearchParams = loanSearchParams;
  const clonedProducts = JSON.parse(JSON.stringify(preProducts));
  const searchParams = useSafeSearchParams();
  const tabToCheck = searchParams?.get(`${cmsQueryParamsPrefix}activeTab`) ?? 1;
  const rewardModalComponentKey = `ComparisonTable_${blockIndex}`;

  if (props.vertical === 'PERSONAL_LOANS' && !loanSearchParamsToWork) {
    loanSearchParamsToWork = getLoanSearchParamsWithDefaultComponentParam(
      brand,
      loanSearchParams,
      {
        amount: props.tableConfig?.default_loan_amount
          ? props.tableConfig?.default_loan_amount.toString()
          : undefined,
        tenure: props.tableConfig?.default_loan_tenure
          ? props.tableConfig?.default_loan_tenure.toString()
          : undefined,
      },
    );
  }

  const [activeAttributes, setActiveAttributes] = useState<SystemAttribute[]>(
    [],
  );

  const { _calculateError, serverProducts } = useMemo<{
    _calculateError: boolean;
    serverProducts: ComparisonTableProduct[];
  }>(() => {
    if (
      props.vertical === 'PERSONAL_LOANS' &&
      pageData.calculatedPersonalLoans
    ) {
      const result = setPLAttributeValues(
        pageData.calculatedPersonalLoans,
        clonedProducts,
      );

      return {
        _calculateError: result.calculateError,
        serverProducts: result.products,
      };
    }

    return {
      _calculateError: false,
      serverProducts: clonedProducts,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preProducts, pageData.calculatedPersonalLoans]);

  const [filteredProducts, setProductsToShow] = useState(serverProducts);

  const hasApprovalOddsColumn = getHasApprovalOddsColumn(rest.tableConfig);

  const { data: approvalOddsData, sendConsent } = useApprovalOdds(
    serverProducts,
    hasApprovalOddsColumn,
    langPrefix,
  );

  const { onRetrieveApprovalOdds, oddsConsentModal } = useApprovalOddsRetrieval(
    {
      approvalOddsData,
      sendConsent,
      langPrefix,
      chcBaseUrl: publicCmsConfig.chcBaseUrl,
    },
  );

  const handleOpenOddsDrawer = () => oddsDrawerRef.current?.open();

  const [calculateError, setCalculateError] = useState(_calculateError);

  const setProductsToShowGlobal = (ps: ComparisonTableProduct[]) => {
    let productsToSave = ps;

    if (tabs?.length) {
      productsToSave = ps.filter((p) => p.tab === +tabToCheck);
    }
    setProductsToShow(productsToSave);

    if (tabs?.length && productsToSave?.length) {
      const firstProductWithAttributes = productsToSave.find(
        (prod) => prod.attributes?.length,
      );
      if (!firstProductWithAttributes) {
        return;
      }

      const tabAttrs =
        firstProductWithAttributes.attributes_from_meta
          ?.map((a) => a.attribute)
          .filter((x) => !!x) ?? [];

      setActiveAttributes(tabAttrs);
    }
  };

  const onFinishedEnterFilter = async ({
    tenure,
    amount,
  }: LoanSearchParams) => {
    const productsIds = filteredProducts.map((p) => p.title!);
    try {
      const res = await feApiFrontClient.calculatePersonalLoans({
        productIds: productsIds,
        tenure: +tenure,
        amount: +amount,
        isDraft: isPreview,
      });
      setCalculateError(false);
      if (!res) {
        return;
      }
      const products_ = (serverProducts ?? [])?.map((prod, i) => {
        const newProduct = { ...prod };
        const resultAttr = res[i];
        newProduct.attributes = RecalculateProductAttributes(
          newProduct.attributes,
          resultAttr,
        );
        return newProduct;
      });
      setProductsToShowGlobal(products_);
    } catch (e) {
      setCalculateError(true);
      return;
    }
  };

  useEffect(() => {
    setProductsToShowGlobal(serverProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabToCheck]);

  const { loanSearchParams: changedLoanSearchParams } = useLoanSearchParams(
    loanSearchParamsToWork as LoanSearchParams,
  );
  const { openModal: openRewardModal } = useOpenRewardModal();
  const { modalProductUrlAlias } = useProductBlockAsideModal();
  // "_pdp" keeps the key distinct from the per-product RewardAsideModal below,
  // which shares the same rewardModal hash (CMS-1924).
  const { openProductDetail, productDetailModal, openProductUid } =
    useProductDetailModal({
      componentUniqueKey: `${rewardModalComponentKey}_pdp`,
    });
  // The PDP modal never sets `modalProductUrlAlias`, so a marked product's row
  // needs the second check to stay highlighted while its modal is open, the
  // way an unmarked product's row does with the legacy panel (CMS-1924).
  const isRowSelected = (product: ComparisonTableProduct) =>
    (!!product.url_alias && product.url_alias === modalProductUrlAlias) ||
    (!!product.uid && product.uid === openProductUid);

  const sendRowClickAnalytics = (
    product: ComparisonTableProduct,
    rowIndex: number,
    componentTitle: string,
  ) =>
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle,
      componentType: ComponentTypeEnum.TABLE,
      event: 'product_interaction',
      index: rowIndex.toString(),
      product,
      url: slug,
      pushDataLayer,
      approvalDegree: product.title
        ? approvalOddsData?.odds[product.title]
        : undefined,
      approvalDegreeState: approvalOddsData?.userState,
    });

  const onRewardColumnClick: OnRewardColumnClickHandler = (
    e,
    product,
    rowIndex,
    clickInfo,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    sendRowClickAnalytics(
      product,
      rowIndex,
      getRewardColumnComponentTitle(clickInfo),
    );

    if (
      vertical === 'PERSONAL_LOANS' &&
      (product.welcome_offers?.length ||
        product.exclusive_offers?.length ||
        product.bundle_exclusive_offers?.length)
    ) {
      openRewardModal(product.uid ?? '', rewardModalComponentKey);
      return;
    }

    openProductDetail(product, SingSaverRewardsSectionId);
  };

  const onRowClick: OnRowClickHandler = (product, rowIndex) => {
    if (
      vertical === 'PERSONAL_LOANS' &&
      (product.welcome_offers?.length ||
        product.exclusive_offers?.length ||
        product.bundle_exclusive_offers?.length)
    ) {
      sendRowClickAnalytics(
        product,
        rowIndex,
        'Product List/Footer_See all Details',
      );
      openRewardModal(product.uid ?? '', rewardModalComponentKey);
      return;
    }
    if (product.url_alias) {
      sendRowClickAnalytics(
        product,
        rowIndex,
        'Product List/Footer_See all Details',
      );
      openProductDetail(product, null);
    }
  };

  const onRefreshFooterButtonClick = () => {
    pushDataLayer({
      event: 'product_interaction',
      action: UserActionEnum.CLICK,
      componentType: ComponentTypeEnum.TABLE,
      component_title: 'Product List/Approval Odds Column_Refresh Live',
    });
    onRetrieveApprovalOdds();
  };

  return (
    <TabsWrapper tabs={tabs}>
      <PersonalLoanFilterWrapper
        vertical={vertical}
        defaultParams={loanSearchParams!}
        onFinishedEnterFilter={onFinishedEnterFilter}
        max_loan_amount={max_loan_amount}
        max_loan_tenure={max_loan_tenure}
        min_loan_amount={min_loan_amount}
        min_loan_tenure={min_loan_tenure}
      >
        <ComparisonTable
          {...rest}
          loanSearchParams={changedLoanSearchParams}
          products={filteredProducts}
          calculateError={calculateError}
          activeAttribute={activeAttributes}
          onRewardColumnClick={onRewardColumnClick}
          onRowClick={onRowClick}
          isRowSelected={isRowSelected}
          pushDataLayer={pushDataLayer}
          approvalOddsData={approvalOddsData ?? undefined}
          onApprovalOddsUnlockClick={onRetrieveApprovalOdds}
          onApprovalOddsClick={handleOpenOddsDrawer}
          slug={slug}
          footer={
            <ApprovalOddsFooter
              approvalOddsData={approvalOddsData}
              onRefresh={onRefreshFooterButtonClick}
            />
          }
        />
        <ApprovalOddsDrawer
          slideUpModalRef={oddsDrawerRef}
          lastUpdated={approvalOddsData?.lastUpdated ?? undefined}
          isOutdated={approvalOddsData?.userState !== 'fresh'}
          onClickCTA={onRetrieveApprovalOdds}
          pushDataLayer={pushDataLayer}
        />
        {oddsConsentModal}
        {productDetailModal}
        {filteredProducts.length
          ? filteredProducts
              //filtering out duplicates
              .filter(
                (p, i) =>
                  filteredProducts.findIndex((p2) => p2.uid === p.uid) === i,
              )
              .map((product) => {
                const welcomeOffers = filterOffersByAmountAndTenure(
                  product.welcome_offers ?? [],
                  changedLoanSearchParams?.amount,
                  changedLoanSearchParams?.tenure,
                );
                const exclusiveOffers = filterOffersByAmountAndTenure(
                  [
                    ...(product.exclusive_offers ?? []),
                    ...(product.bundle_exclusive_offers ?? []),
                  ],
                  changedLoanSearchParams?.amount,
                  changedLoanSearchParams?.tenure,
                );

                return (
                  <div key={product.uid}>
                    <RewardAsideModal
                      welcomeOffers={welcomeOffers}
                      exclusiveOffers={exclusiveOffers}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      loanSearchParams={changedLoanSearchParams ?? ({} as any)}
                      product={product}
                      keyFromComponent={rewardModalComponentKey}
                      vertical={vertical}
                      pushDataLayer={pushDataLayer}
                      ctaLinkOverride={product.override_cta_link}
                      // TODO: if ComparisonTable will be used for Car Insurance in the future, we need to pass the component here as well
                      carInsuranceCtaComponent={null}
                      priceLine={null}
                      approvalOdds={
                        product.title
                          ? approvalOddsData?.odds[product.title]
                          : undefined
                      }
                      approvalOddsState={approvalOddsData?.userState}
                    />
                  </div>
                );
              })
          : null}
      </PersonalLoanFilterWrapper>
    </TabsWrapper>
  );
}

const getHasApprovalOddsColumn = (
  tableConfig: Omit<ComponentComparisonTable, '__typename'>,
): boolean =>
  (tableConfig.baseColumnOrder?.some(
    (col) => col.column?.keyColumn === 'APPROVAL_ODDS',
  ) ||
    tableConfig.personal_loan_order?.item?.some(
      (x) => x.column === 'APPROVAL_ODDS',
    )) ??
  false;
